
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { SocialBlockConfiguration } from '@/types/block-configurations';

const PLATFORM_COLORS: Record<string, string> = {
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  instagram: '#E1306C',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  pinterest: '#E60023',
  tiktok: '#010101',
  github: '#181717',
};

export function SocialBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as SocialBlockConfiguration;

  const iconSize = config.iconSize ?? 32;
  const iconSpacing = config.iconSpacing ?? 10;
  const isHorizontal = config.layout !== 'vertical';

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      onMouseEnter={(e) => { e.stopPropagation(); onHover(block.id); }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-social cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        paddingTop: `${config.padding?.top ?? 10}px`,
        paddingRight: `${config.padding?.right ?? 10}px`,
        paddingBottom: `${config.padding?.bottom ?? 10}px`,
        paddingLeft: `${config.padding?.left ?? 10}px`,
        textAlign: config.alignment?.horizontal || 'center',
      }}
    >
      {config.links && config.links.length > 0 ? (
        <div
          className={isHorizontal ? 'flex flex-wrap' : 'flex flex-col'}
          style={{
            gap: `${iconSpacing}px`,
            justifyContent: config.alignment?.horizontal === 'left' ? 'flex-start' : config.alignment?.horizontal === 'right' ? 'flex-end' : 'center',
            alignItems: 'center',
          }}
        >
          {config.links.map((link, i) => {
            const color = PLATFORM_COLORS[link.platform] || '#888888';
            const radius = config.iconShape === 'circle' ? '50%' : config.iconShape === 'rounded' ? '8px' : '0';
            return (
              <a
                key={i}
                href={link.url}
                onClick={(e) => e.preventDefault()}
                title={link.label || link.platform}
                className="inline-flex items-center justify-center"
                style={{
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  borderRadius: radius,
                  // Backend always uses MJML's colored brand icons
                  backgroundColor: color,
                  color: '#ffffff',
                  fontSize: `${Math.round(iconSize * 0.5)}px`,
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  flexShrink: 0,
                }}
              >
                {link.platform.slice(0, 2).toUpperCase()}
              </a>
            );
          })}
        </div>
      ) : (
        <div className="flex justify-center gap-2">
          {['FB', 'TW', 'IG'].map((label) => (
            <div
              key={label}
              className="inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-bold"
              style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
            >
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
